import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPost, apiPut, ApiError } from "../api";
import type { ID } from "../id";
import type { SystemRole } from "../../auth/queries";
import type { RegistrationFlow } from "./registration";

/**
 * Who may log in and which registration doors are open
 * (`internal/service/access`). Admin always may log in and is never
 * listed in `login_roles`.
 */
export interface AccessSettings {
  login_restricted: boolean;
  login_roles: SystemRole[];
  exceptions: { user_id: ID; email: string; display_name: string }[];
  closed_flows: RegistrationFlow[];
}

export interface AccessPayload {
  login_restricted: boolean;
  login_roles: SystemRole[];
  exceptions: ID[];
  closed_flows: RegistrationFlow[];
}

/** What a save does to the people signed in right now. */
export interface AccessImpact {
  users: number;
  sessions: number;
}

export function useAccessSettings() {
  return useQuery<AccessSettings>({
    queryKey: ["access"],
    queryFn: () => apiGet<AccessSettings>("/access"),
  });
}

/** Asks before saving whom the save would log out. Writes nothing. */
export function usePreviewAccess() {
  return useMutation<AccessImpact, ApiError, AccessPayload>({
    mutationFn: (payload) => apiPost<AccessImpact>("/access/preview", payload),
    meta: { suppressErrorToast: true },
  });
}

export function useSaveAccess() {
  const qc = useQueryClient();
  return useMutation<AccessImpact, ApiError, AccessPayload>({
    mutationFn: (payload) => apiPut<AccessImpact>("/access", payload),
    // Rendered on the page beside the button, or in the confirm dialog.
    meta: { suppressErrorToast: true },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["access"] });
      qc.invalidateQueries({ queryKey: ["registration", "flows"] });
    },
  });
}

/** Which doors of the public registration take a new club. No session. */
export function useOpenRegistrationFlows() {
  return useQuery<RegistrationFlow[]>({
    queryKey: ["registration", "flows"],
    queryFn: async () =>
      (await apiGet<{ open_flows: RegistrationFlow[] }>("/registrace-tymu/toky")).open_flows,
  });
}
